import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import "../../../design/tableUser.css";
import "./styles.scss";
import { mainAxios } from "../../../services/mainAxios";
import "react-datepicker/dist/react-datepicker.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faChevronLeft, faChevronRight, faTimes } from "@fortawesome/free-solid-svg-icons";
import { useAppContext } from "../../../AppContext";
import { toast } from "react-toastify";
import Accordion from "../../CustomInvoices/WizardSteps/Accordion";
import { useTranslation } from "react-i18next";
import Loader from "../../Layout/Loader";

const UsersTable = ({ state, setState,refreshUser }) => {
  const { t } = useTranslation("translations");
  const [loadingTcr, setLoadingTcr] = useState(false)
  const [loadingCompanies, setLoadingCompanies] = useState(true)
  const { user: [loggedInUser], selectedCompany: [selectedCompany], getUser } = useAppContext()
  const [tableRows, setTableRows] = useState({
    totalRows: 10,
    rowsPerPage: 10,
    currentPage: 1,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [showOnlyWithTcrRights, setShowOnlyWithTcrRights] = useState(false);
  const [drawerState, setDrawerState] = useState({ open: false, company: null, branch: null });

  let companiesForTable = state?.companiesForTable
  let userCompaniesRights = state?.user?.companiesRights

  const getTcrRights = useCallback((user) => {
    let selectedUserCompaniesRights=user?.companiesRights ?? userCompaniesRights
    let rights = []
    selectedUserCompaniesRights.forEach((company) => {
      company.branches.forEach((branch) => {
        branch.tcrTypes.forEach((tcr) => {
          rights.push(tcr.id)
        })
      })
    })
    return rights
  }, [companiesForTable, userCompaniesRights])

  const isLoggedInUser = state?.user?.id == loggedInUser?.id
  const [tcrRights, setTcrRights] = useState(getTcrRights())

  useEffect(()=>{
    setTcrRights(getTcrRights())
  },[state?.user?.companiesRights])
  
  const updateUserAccess = () => {
    setLoadingTcr(true)
    let body = {
      object: "UpdateUserAccess",
      operation: JSON.stringify({ userId: state.user.id, tcrRights: tcrRights.join(",") }),
      nuis: loggedInUser.companiesRights.find(
        (company) => company.id === parseInt(selectedCompany)
      )?.nuis,
    }
    mainAxios.post('apiEndpoint/saveOrUpdate', body).then(async (res) => {
      setLoadingTcr(false)
      if (res.data.status == true) {
        const currentUser=await refreshUser()
        setTcrRights(getTcrRights(currentUser))
        if(isLoggedInUser) {
          getUser()
        }
        toast.success(t(['toast.userUpdate']), {
          containerId: "all_toast"
        })
      } else {
        setTcrRights(getTcrRights())
      }
    })
      .catch((err) => {
        setLoadingTcr(false)
        setTcrRights(getTcrRights())
      })
  }

  const firstUpdate = useRef(true);

  useEffect(() => {
    if(showOnlyWithTcrRights){
      return
    }
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    const indicator = setTimeout(() => {
      getFilteredResults(10, 1, false)
    }, 400);

    return () => {
      clearTimeout(indicator);
    };
  }, [searchQuery]);

  useEffect(() => {
    getFilteredResults(tableRows.rowsPerPage, tableRows.currentPage);

  }, [tableRows.currentPage, tableRows.rowsPerPage])

  const getFilteredResults = (rowsPerPage = 10, currentPage = 1) => {
    setLoadingCompanies(true)
    mainAxios
      .post("apiEndpoint/search", {
        object: "GetCompany",
        value: "",
        params: {
          companyLiveSearch: searchQuery
        },
        pagination: {
          pageSize: rowsPerPage,
          pageNumber: currentPage,
        },
      })
      .then((res) => {
        if (res.data.status) {
          let companies = res.data.content;
          let userCompanies = loggedInUser.companiesRights;
          let companiesForTable = companies;
          if (loggedInUser?.role?.id != 1) {
            companiesForTable = companiesForTable.filter((t2) =>
              userCompanies.find((t1) => t1?.id == t2?.id)
            );
          }
          setState((state) => ({
            ...state,
            companiesForTable
          }))
          setTableRows((tableRows) => ({
            ...tableRows,
            totalRows: res?.data?.totalSize,
          }));

        }
      })
      .finally(() => {
        setLoadingCompanies(false)
      });
  }

  const renderSelectionButton = (label, onClick, variant = "light") => (
    <button
      type="button"
      className={`btn btn-sm ${variant === "primary" ? "btn-primary" : variant === "muted" ? "users-table-btn-muted" : "btn-light"} users-table-action-btn`}
      onClick={onClick}
    >
      {label}
    </button>
  );

  const toggleTcrSelection = (tcrId) => {
    let foundTcr = tcrRights.find((tcrCode) => tcrCode == tcrId);
    if (foundTcr) {
      let filteredTcrRights = tcrRights.filter((tcr) => tcr != foundTcr);
      setTcrRights(filteredTcrRights);
    } else {
      setTcrRights([...tcrRights, tcrId]);
    }
  };

  const toggleBranchSelection = (branch) => {
    const branchTcrIds = branch.tcrTypes.map((tcr) => tcr.id);
    const hasUnselectedTcr = branchTcrIds.some((tcrId) => !tcrRights.includes(tcrId));

    if (hasUnselectedTcr) {
      setTcrRights(Array.from(new Set([...tcrRights, ...branchTcrIds])));
      return;
    }

    setTcrRights(tcrRights.filter((tcrId) => !branchTcrIds.includes(tcrId)));
  };

  const openBranchDrawer = (company, branch) => {
    setDrawerState({ open: true, company, branch });
  };

  const closeBranchDrawer = () => {
    setDrawerState({ open: false, company: null, branch: null });
  };

  const renderCompanyItem = (company) => {
    if (company.branches.length == 0) {
      return <div className="users-table-empty-state">{t("invoice.noBranchFound")}</div>
    }
    const selectedBranchesCount = company.branches.filter((branch) =>
      branch.tcrTypes.some((tcr) => tcrRights.includes(tcr.id))
    ).length;

    return (
      <div className="users-company-panel">
        <div className="users-company-toolbar">
          <div className="users-company-toolbar-meta">
            <span className="users-table-section-title">{t("home.branches")}</span>
            <span className="users-table-section-subtitle">
              {company.branches.length} {company.branches.length === 1 ? "branch" : "branches"}, {selectedBranchesCount} selected
            </span>
          </div>
          <div className="users-table-actions">
            {renderSelectionButton("Unselect All Company Tcrs", () => {
              let newTcrRights = [];
              company.branches.forEach(branch => {
                branch.tcrTypes.forEach(tcr => {
                  if (newTcrRights) newTcrRights.push(tcr.id);
                });
              });
              let filtered = tcrRights.filter(tcr => !newTcrRights.includes(tcr));
              setTcrRights(filtered);
            }, "muted")}
            {renderSelectionButton("Select All Company Tcrs", () => {
              let newTcrRights = [];
              company.branches.forEach(branch => {
                branch.tcrTypes.forEach(tcr => {
                  if (newTcrRights) newTcrRights.push(tcr.id);
                });
              });
              let uniqueTcrRights = Array.from(new Set([...tcrRights, ...newTcrRights]));
              setTcrRights(uniqueTcrRights);
            }, "primary")}
          </div>
        </div>

        <div className="users-branches-list">
          {company.branches.map((branch) => {
            const branchTcrIds = branch.tcrTypes.map((tcr) => tcr.id);
            const selectedBranchTcrCount = branchTcrIds.filter((tcrId) =>
              tcrRights.find((selectedId) => selectedId == tcrId)
            ).length;
            const hasBranchTcrs = branch.tcrTypes.length > 0;
            const isBranchFullySelected = hasBranchTcrs && selectedBranchTcrCount === branch.tcrTypes.length;

            return (
              <button
                type="button"
                key={branch.id}
                className={`users-branch-row ${selectedBranchTcrCount > 0 ? "users-branch-row-selected" : "users-branch-row-unselected"}`}
                onClick={() => openBranchDrawer(company, branch)}
              >
                <div className="users-branch-row-main">
                  <span className="users-branch-row-name">{branch.sellerName}</span>
                  <span className="users-branch-row-code">{branch?.businUnitCode}</span>
                </div>
                <div className="users-branch-row-actions">
                  <span className="users-branch-row-meta">
                    {selectedBranchTcrCount}/{branch.tcrTypes.length} selected
                  </span>
                  <button
                    type="button"
                    className={`users-branch-inline-toggle ${isBranchFullySelected ? "users-branch-inline-toggle-active" : ""}`}
                    disabled={!hasBranchTcrs}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!hasBranchTcrs) return;
                      toggleBranchSelection(branch);
                    }}
                  >
                    {!hasBranchTcrs ? "No TCR" : isBranchFullySelected ? "Unselect" : "Select"}
                  </button>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    )
  };

  const activeDrawerBranch = drawerState.branch;
  const activeDrawerCompany = drawerState.company;
  const activeDrawerTcrIds = activeDrawerBranch?.tcrTypes?.map((tcr) => tcr.id) ?? [];
  const selectedDrawerTcrCount = activeDrawerTcrIds.filter((tcrId) =>
    tcrRights.find((selectedId) => selectedId == tcrId)
  ).length;

  const displayedCompanies = showOnlyWithTcrRights ? (userCompaniesRights ?? []) : (companiesForTable ?? []);
  const filteredCompanies = useMemo(() => {
    if (!showOnlyWithTcrRights || !searchQuery) return displayedCompanies;
    return displayedCompanies.filter(c => c.name?.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [displayedCompanies, searchQuery, showOnlyWithTcrRights]);
  const totalVisibleCompanies = showOnlyWithTcrRights ? filteredCompanies.length : (tableRows.totalRows || 0);
  const totalPages = Math.max(1, Math.ceil(totalVisibleCompanies / tableRows.rowsPerPage));
  const paginatedCompanies = showOnlyWithTcrRights
    ? filteredCompanies.slice(
      (tableRows.currentPage - 1) * tableRows.rowsPerPage,
      tableRows.currentPage * tableRows.rowsPerPage
    )
    : filteredCompanies;
  const selectedCount = tcrRights.length;

  const onPageChange = (direction) => {
    setTableRows((prev) => ({
      ...prev,
      currentPage:
        direction === "next"
          ? Math.min(prev.currentPage + 1, totalPages)
          : Math.max(prev.currentPage - 1, 1),
    }));
  };

  useEffect(() => {
    if (tableRows.currentPage > totalPages) {
      setTableRows((prev) => ({
        ...prev,
        currentPage: totalPages,
      }));
    }
  }, [tableRows.currentPage, totalPages]);

  return <div className="companies-table-wrapper users-table-shell shadow mb-4">
    <div className="card-title users-table-header">
      <div>
        <h3 className="users-table-title">{t("navbar.companies")}</h3>
        <p className="users-table-subtitle mb-0">
          {totalVisibleCompanies} {totalVisibleCompanies === 1 ? "company" : "companies"} visible, {selectedCount} TCR selected
        </p>
      </div>
      <div className="custom-control custom-switch users-table-toggle">
        <input
          type="checkbox"
          className="custom-control-input"
          id="showOnlyWithTcrRights"
          checked={showOnlyWithTcrRights}
          onChange={(e) => setShowOnlyWithTcrRights(e.target.checked)}
        />
        <label
          className="custom-control-label"
          htmlFor="showOnlyWithTcrRights"
        >
          {t("users.branchWithRights")}
        </label>
      </div>
    </div>
    <div className="card-body">
      <div className="reports table-green users-table-content">
        <div className="users-table-toolbar">
          <div className="users-table-search-block">
            <label className="users-table-search-label">Search companies</label>
            <div className="users-table-search">
              <input
                type="text"
                className="form-control users-table-search-input"
                placeholder="Search company name..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setTableRows((prev) => ({ ...prev, currentPage: 1 }));
                }}
              />
              {searchQuery && (
                <button
                  type="button"
                  className="users-table-search-clear"
                  onClick={() => {
                    setSearchQuery("");
                    setTableRows((prev) => ({ ...prev, currentPage: 1 }));
                  }}
                  aria-label="Clear search"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="users-table-list-shell">
          {loadingCompanies ? (
            <Loader size="small" />
          ) : (
            <>
              {paginatedCompanies.map((company) => (
                <div key={company.id} className="users-company-card">
                  <Accordion
                    padding="0px"
                    overflow={false}
                    marginBottom="0px"
                    heading={
                      <div className="users-company-heading">
                        <div className="users-company-heading-main">
                          <span className="users-company-name">{company.name}</span>
                        </div>
                        <div className="users-company-heading-meta">
                          <span className="users-company-meta">
                            {company.branches?.length || 0} {(company.branches?.length || 0) === 1 ? "branch" : "branches"}
                          </span>
                          <span className="users-company-meta">
                            {company.branches?.reduce((acc, branch) => acc + branch.tcrTypes.filter((tcr) => tcrRights.includes(tcr.id)).length, 0) || 0} selected
                          </span>
                        </div>
                      </div>
                    }
                  >
                    <div className="users-company-body">
                      {renderCompanyItem(company)}
                    </div>
                  </Accordion>
                </div>
              ))}
              {paginatedCompanies.length === 0 && (
                <div className="users-table-empty-state">{t("common.noResults")}</div>
              )}
            </>
          )}
        </div>

        <div className="users-table-pagination">
          <div className="users-table-pagination-info">
            Page {tableRows.currentPage} of {totalPages}
          </div>
          <div className="users-table-pagination-controls">
            <select
              className="form-control users-table-page-size"
              value={tableRows.rowsPerPage}
              onChange={(e) =>
                setTableRows((prev) => ({
                  ...prev,
                  rowsPerPage: parseInt(e.target.value),
                  currentPage: 1,
                }))
              }
            >
              {[5, 10, 20, 50].map((size) => (
                <option key={size} value={size}>
                  {size} / page
                </option>
              ))}
            </select>
            <button
              type="button"
              className="users-table-pagination-btn"
              disabled={tableRows.currentPage <= 1}
              onClick={() => onPageChange("prev")}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <button
              type="button"
              className="users-table-pagination-btn"
              disabled={tableRows.currentPage >= totalPages}
              onClick={() => onPageChange("next")}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        </div>

        <div className="users-table-footer">
          <button disabled={loadingTcr} className="btn btn-primary users-table-save-btn" onClick={updateUserAccess}>
            <FontAwesomeIcon icon={faCheck} className="mr-2" />
            {t("common.save")}
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => {
            }}
          >
            {t("common.cancel")}
          </button>
        </div>
      </div>
    </div>

    {drawerState.open && (
      <div className="users-branch-drawer-overlay" onClick={closeBranchDrawer}>
        <div className="users-branch-drawer" onClick={(e) => e.stopPropagation()}>
          <div className="users-branch-drawer-header">
            <div>
              <div className="users-branch-drawer-company">{activeDrawerCompany?.name}</div>
              <div className="users-branch-drawer-title">{activeDrawerBranch?.sellerName}</div>
              <div className="users-branch-drawer-subtitle">{activeDrawerBranch?.businUnitCode}</div>
            </div>
            <button type="button" className="users-branch-drawer-close" onClick={closeBranchDrawer}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          <div className="users-branch-drawer-summary">
            <span className="users-table-section-title">TCR</span>
            <span className="users-table-section-subtitle users-table-section-subtitle-strong">
              {selectedDrawerTcrCount}/{activeDrawerBranch?.tcrTypes?.length || 0} selected
            </span>
          </div>

          <div className="users-branch-drawer-body">
            {activeDrawerBranch?.tcrTypes?.length > 0 ? (
              <div className="users-tcr-grid">
                {activeDrawerBranch.tcrTypes.map((tcrType) => {
                  const isChecked = tcrRights.find((tcrCode) => tcrCode == tcrType.id) || false;

                  return (
                    <div
                      key={tcrType.id}
                      className={`users-tcr-item ${isChecked ? "users-tcr-item-selected" : ""}`}
                      onClick={() => toggleTcrSelection(tcrType.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          toggleTcrSelection(tcrType.id);
                        }
                      }}
                    >
                      <input
                        type="checkbox"
                        id={`dataFrom_${tcrType.id}`}
                        className="users-tcr-input"
                        checked={isChecked}
                        onChange={() => toggleTcrSelection(tcrType.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <label
                        htmlFor={`dataFrom_${tcrType.id}`}
                        className={`users-tcr-label w-100 ${tcrType.status < 100 ? "text-danger" : ""}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span className="users-tcr-label-main">
                          <span className="users-tcr-code">{tcrType?.tcrCode}</span>
                        </span>
                        {tcrType.status < 100 && (
                          <span className="users-tcr-status">Inactive</span>
                        )}
                      </label>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="users-table-empty-state">{t("invoice.noTcrFound")}</div>
            )}
          </div>
        </div>
      </div>
    )}
  </div>
};


export default UsersTable;
